import * as React from 'react';
import { BaSeDataSelectionContext } from '../../../contexts/data/selection';
import { BaSeCheckbox } from '../../input/checkbox/checkbox';
import { DataInterfaceProps } from '../data-styled';

export interface ToggleAllCheckboxProps<Item>
  extends Pick<DataInterfaceProps, 'highlightedColor'> {
  actualItems: Item[];
  rowIdKey: keyof Item;
}

export function DataToggleAllCheckbox<Item extends object>({
  highlightedColor,
  actualItems,
  rowIdKey,
}: React.PropsWithoutRef<ToggleAllCheckboxProps<Item>>): JSX.Element {
  const {
    isAllSelected,
    isAllFromPageSelected,
    selectedItems,
    setIsAllFromPageSelected,
    setSelectedItems,
  } = React.useContext(BaSeDataSelectionContext);

  const checked = React.useMemo(
    () => isAllSelected || isAllFromPageSelected,
    [isAllSelected, isAllFromPageSelected],
  );

  const indeterminate = React.useMemo(
    () =>
      actualItems.some((actualItem) =>
        selectedItems.some(
          (selectedItem) => selectedItem[rowIdKey] === actualItem[rowIdKey],
        ),
      ),
    [actualItems, selectedItems],
  );

  React.useEffect(
    () =>
      setIsAllFromPageSelected(
        isAllSelected ||
          (selectedItems.length > 0 &&
            actualItems.every((actualItem) =>
              selectedItems.some(
                (selectedItem) =>
                  selectedItem[rowIdKey] === actualItem[rowIdKey],
              ),
            )),
      ),
    [isAllSelected, actualItems, selectedItems, rowIdKey],
  );

  return isAllSelected || actualItems.length === 0 ? (
    <></>
  ) : (
    <BaSeCheckbox
      color={highlightedColor}
      checked={checked || indeterminate}
      indeterminate={!checked && indeterminate}
      onChange={(changedChecked) => {
        setSelectedItems((actualSelectedItems) =>
          actualSelectedItems.filter((actualSelectedItem) =>
            actualItems.every(
              (actualItem) =>
                actualItem[rowIdKey] !== actualSelectedItem[rowIdKey],
            ),
          ),
        );
        if (changedChecked) {
          setSelectedItems((actual) => [...actual, ...actualItems]);
        }
      }}
    />
  );
}
